import {Link, Outlet, useLocation, useNavigate} from 'react-router-dom';
import {useCan, usePersona} from 'src/permissions/hooks';
import {AMPNavbar} from '../../components/common/AMPNavbar';
import {AMPDrawer} from '../../components/common/AMPDrawer';
import {Box, Container, DrawerProps, SvgIcon} from '@mui/material';
import {useBusinessId, useAppDispatch, useAppSelector} from 'src/app/hooks';
import {useEffect, useState} from 'react';
import {AppRouteEnum, AppRouteSpace} from 'src/utils/helper';
import {
    contentSx,
    fullWidthContentSx,
    notificationsBoxSx,
    notificationsNumberSx,
    notificationsSx,
    pageContentSx,
    pageWrapperSx,
} from './DashboardLayout.sx';
import {DashboardLayoutProvider} from './DashboardLayout.context';
import {useScreenTier} from 'src/hooks/useScreenTier';
import {NotificationsIcon} from '../../components/icons/ToolbarIcons';
import {NotificationMarker} from '../../components/common/NotificationMarker';
import {usersApi} from 'src/api';
import {setAuthInfo} from 'src/stores/common/commonSlice';
import {BottomNav} from 'src/components/mobile/BottomNav';

export const DashboardLayout = () => {
    const activeBusiness = useAppSelector((state) => state.common.activeBusiness);
    const canSearchNotifications = useCan('notifications.search');
    // The business being worked in: the caller's own, or the one a platform administrator picked.
    // Reading `authInfo.business.id` directly was a crash for them - they have no business of
    // their own, and null has no id.
    const businessId = useBusinessId();
    const authInfo = useAppSelector((state) => state.common.authInfo);
    const dispatch = useAppDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const persona = usePersona();
    const isDrawerMustHide = (authInfo?.contracts?.length && persona.isDriver) || !persona.isDriver;
    const [openDrawer, setOpenDrawer] = useState<boolean>(false);
    const [drawerVariant, setDrawerVariant] = useState<DrawerProps['variant']>('temporary');
    // One source of truth for the width. `responsive` keeps the meaning it always had - anything
    // narrower than a desktop - so the tables reading it through the context are untouched.
    const {isDesktop} = useScreenTier();
    const responsive = !isDesktop;
    const [getDriver] = usersApi.useLazyGetUserByIdQuery();

    const getCurrentDriver = async () => {
        if (persona.isDriver) {
            try {
                const response = await getDriver({
                    userId: authInfo.id,
                    businessId: businessId,
                }).unwrap();

                dispatch(setAuthInfo(response));
            } catch (e) {
                console.error('COULD NOT GET CURRENT DRIVER', e);
            }
        }
    };

    const handleOpenDrawer = () => {
        setOpenDrawer(true);
    };

    const handleCloseDrawer = () => {
        setOpenDrawer(false);
    };

    useEffect(() => {
        if (!Object.keys(authInfo).length) {
            localStorage.clear();
            navigate(AppRouteSpace([AppRouteEnum.login]));
        }
    }, [authInfo]);

    useEffect(() => {
        void getCurrentDriver();
    }, [location]);

    // The drawer follows the width. This used to be a resize listener registered inside an effect
    // with no dependency array, so it was removed and added again on every single render.
    useEffect(() => {
        if (isDesktop) {
            setDrawerVariant('permanent');
            handleOpenDrawer();
        } else {
            setDrawerVariant('temporary');
            handleCloseDrawer();
        }
    }, [isDesktop]);

    return (
        <DashboardLayoutProvider value={responsive}>
            <Box sx={pageWrapperSx}>
                {isDrawerMustHide && (
                    <AMPDrawer
                        handleCloseDrawer={handleCloseDrawer}
                        drawerVariant={drawerVariant}
                        open={openDrawer}
                    />
                )}
                <Box sx={pageContentSx}>
                    {canSearchNotifications && (
                        <Box sx={notificationsBoxSx}>
                            <Box sx={notificationsSx}>
                                <Box sx={notificationsNumberSx}>
                                    <NotificationMarker/>
                                </Box>
                                <Link to={AppRouteSpace([AppRouteEnum.app, AppRouteEnum.notifications])}>
                                    <SvgIcon
                                        component={NotificationsIcon}
                                        viewBox="0 0 20 20"
                                    />
                                </Link>
                            </Box>
                        </Box>
                                        )}
                    {location.pathname !== AppRouteSpace([AppRouteEnum.app, AppRouteEnum.welcome]) && (
                        <AMPNavbar handleOpenDrawer={handleOpenDrawer}/>
                    )}
                    {/* Also allowed to be narrower than its content, for the same reason the shell is:
                        one link in the chain without it hands the full width back down. */}
                    <Box sx={{minWidth: 0}}>
                        <Container
                            // The cap lives in the sx, not in a breakpoint name: `xl` is 1536px and
                            // there is no larger one to move to.
                            maxWidth={false}
                            sx={
                                location.pathname !== AppRouteSpace([AppRouteEnum.app, AppRouteEnum.welcome])
                                    ? contentSx
                                    : fullWidthContentSx
                            }
                        >
                            {/* Keyed on the business, so switching it remounts the page rather than
                                leaving it on screen with another tenant's numbers. Clearing the caches
                                is not enough on its own: most pages fetch from an effect that runs on
                                mount, and an effect does not re-run because a cache was emptied. The
                                key also discards the filters and page number of the business just
                                left, which belonged to it and not to this one. */}
                            <Outlet key={activeBusiness}/>
                        </Container>
                    </Box>
                </Box>
                {/* The phone's and the tablet's permanent navigation. Fixed to the bottom, so the
                    content column carries the matching clearance in `pageContentSx`. */}
                <BottomNav/>
            </Box>
        </DashboardLayoutProvider>
    );
};
